/**
 * The data the gateway feeds, normalised, and a pure reducer that applies
 * READY and every dispatch event to it. The Zustand store (store/data.ts)
 * only wraps this, so tests can drive it with plain objects.
 */
import {
  compareSnowflakes,
  isThreadChannelType,
  MessageFlags,
  RelationshipType,
  type Channel,
  type GatewayDispatch,
  type GatewayGuild,
  type Guild,
  type GuildMember,
  type Message,
  type Presence,
  type PrivateUser,
  type ReadState,
  type Relationship,
  type Snowflake,
  type User,
  type UserGuildSettings,
  type UserSettings,
  type VoiceState,
  type Reaction,
  type Emoji,
} from '@neosocial/protocol';
import { applyThreadDispatch, bumpThread, dropThreadsOf, threadCountDown } from '../threads/store';
import { applyPollVote, keepMyVotes } from './pollMath';
import { mentionAllowed, mentionKindsOf } from '../notify/rules';

export interface ChannelMessages {
  /** Oldest first. */
  ids: Snowflake[];
  byId: Record<Snowflake, Message>;
  /** True once the first page has loaded. */
  loaded: boolean;
  /** True while older history may exist. */
  hasMore: boolean;
  /**
   * True when the loaded window ends before the newest message: a jump to
   * an old message loaded the page around it (Discord's "viewing older
   * messages"). New messages are then not appended (there is a gap); the
   * newer pages load as you scroll down, or "Jump to present" reloads.
   */
  hasNewer?: boolean;
  /**
   * Set by a fresh READY (a new gateway session: anything may have been
   * sent, edited or deleted meanwhile). Still shown; the next load fetches
   * the latest page again and reconciles.
   */
  stale?: boolean;
}

export interface DataState {
  me: PrivateUser | null;
  settings: UserSettings | null;
  /** Guild objects without the big arrays (those are normalised below). */
  guilds: Record<Snowflake, Guild>;
  guildOrder: Snowflake[];
  channels: Record<Snowflake, Channel>;
  /** guildId -> userId -> member */
  members: Record<Snowflake, Record<Snowflake, GuildMember>>;
  users: Record<Snowflake, User>;
  /** userId -> presence (one community in the alpha, so not per guild) */
  presences: Record<Snowflake, Presence>;
  /** guildId -> userId -> voice state (only users in a channel) */
  voiceStates: Record<Snowflake, Record<Snowflake, VoiceState>>;
  relationships: Record<Snowflake, Relationship>;
  readStates: Record<Snowflake, ReadState>;
  messages: Record<Snowflake, ChannelMessages>;
  /** channelId -> userId -> unix ms when the typing indicator ends */
  typing: Record<Snowflake, Record<Snowflake, number>>;
  /** Bumped on CHANNEL_PINS_UPDATE so an open pins list refetches. */
  pinsVersion: Record<Snowflake, number>;
  /**
   * "View server as role" (Server settings, owner and Administrators): while
   * set, the signed-in user's permissions in that guild are computed as if
   * they held exactly these roles and were not the owner. A preview only:
   * the server still checks every action with the real roles.
   */
  viewAs: ViewAs | null;
  /**
   * Notification settings per server (2026-10-29, notify/): only servers
   * where the user set something; READY and USER_GUILD_SETTINGS_UPDATE.
   */
  guildSettings: Record<Snowflake, UserGuildSettings>;
}

export interface ViewAs {
  guildId: Snowflake;
  roleIds: Snowflake[];
}

export const emptyData = (): DataState => ({
  me: null,
  settings: null,
  guilds: {},
  guildOrder: [],
  channels: {},
  members: {},
  users: {},
  presences: {},
  voiceStates: {},
  relationships: {},
  readStates: {},
  messages: {},
  typing: {},
  pinsVersion: {},
  viewAs: null,
  guildSettings: {},
});

/** How long a TYPING_START shows (Discord: about 10 s). */
export const TYPING_MS = 10_000;

function withUser(
  users: Record<Snowflake, User>,
  u: User | undefined | null,
): Record<Snowflake, User> {
  if (!u) return users;
  const prev = users[u.id];
  if (
    prev &&
    prev.username === u.username &&
    prev.global_name === u.global_name &&
    prev.avatar === u.avatar
  )
    return users;
  return { ...users, [u.id]: { ...prev, ...u } };
}

/** A message's author and the people it mentions (so a mention shows a name, not an ID). */
function withMessageUsers(users: Record<Snowflake, User>, m: Message): Record<Snowflake, User> {
  let out = withUser(users, m.author);
  for (const u of m.mentions ?? []) out = withUser(out, u);
  return out;
}

/**
 * Takes in one guild as READY or GUILD_CREATE carries it: the guild, its
 * channels (in place of any held for it), members, presences and voice. The
 * guild's presences are whole: a member left out of them is offline, so a
 * presence held from before (while out of the guild nothing updated it) goes.
 */
function withGuild(s: DataState, g: GatewayGuild): DataState {
  const {
    channels,
    members,
    presences,
    voice_states,
    member_count: _mc,
    joined_at: _j,
    threads = [],
    ...guild
  } = g;
  const nextChannels: DataState['channels'] = {};
  for (const [id, c] of Object.entries(s.channels)) if (c.guild_id !== g.id) nextChannels[id] = c;
  for (const c of channels) nextChannels[c.id] = { ...c, guild_id: g.id };
  // the active threads they see (2026-10-29), each with their own membership
  for (const c of threads) nextChannels[c.id] = { ...c, guild_id: g.id };
  const users = { ...s.users };
  const nextPresences = { ...s.presences };
  const mm: Record<Snowflake, GuildMember> = {};
  for (const m of members) {
    mm[m.user.id] = m;
    users[m.user.id] = m.user;
    delete nextPresences[m.user.id];
  }
  for (const p of presences) nextPresences[p.user.id] = p;
  const vs: Record<Snowflake, VoiceState> = {};
  for (const v of voice_states) if (v.channel_id) vs[v.user_id] = { ...v, guild_id: g.id };
  return {
    ...s,
    guilds: { ...s.guilds, [g.id]: guild },
    guildOrder: s.guildOrder.includes(g.id) ? s.guildOrder : [...s.guildOrder, g.id],
    channels: nextChannels,
    users,
    members: { ...s.members, [g.id]: mm },
    presences: nextPresences,
    voiceStates: { ...s.voiceStates, [g.id]: vs },
  };
}

/** Forgets a guild you are no longer in: it, its channels and their history, members, voice. */
function dropGuild(s: DataState, guildId: Snowflake): DataState {
  const guilds = { ...s.guilds };
  delete guilds[guildId];
  const channels: DataState['channels'] = {};
  const messages = { ...s.messages };
  for (const [id, c] of Object.entries(s.channels)) {
    if (c.guild_id === guildId) delete messages[id];
    else channels[id] = c;
  }
  const members = { ...s.members };
  delete members[guildId];
  const voiceStates = { ...s.voiceStates };
  delete voiceStates[guildId];
  return {
    ...s,
    guilds,
    guildOrder: s.guildOrder.filter((g) => g !== guildId),
    channels,
    messages,
    members,
    voiceStates,
    viewAs: s.viewAs?.guildId === guildId ? null : s.viewAs,
  };
}

function sortIds(ids: Snowflake[]): Snowflake[] {
  return ids.slice().sort(compareSnowflakes);
}

function emptyMessages(): ChannelMessages {
  return { ids: [], byId: {}, loaded: false, hasMore: true };
}

/** Inserts or replaces messages, keeping ids in snowflake order. */
export function mergeMessages(
  cm: ChannelMessages | undefined,
  list: Message[],
  page?: { loaded?: boolean; hasMore?: boolean },
): ChannelMessages {
  const base = cm ?? emptyMessages();
  const byId = { ...base.byId };
  const ids = new Set(base.ids);
  for (const m of list) {
    byId[m.id] = m;
    ids.add(m.id);
  }
  return {
    ids: sortIds([...ids]),
    byId,
    loaded: page?.loaded ?? base.loaded,
    hasMore: page?.hasMore ?? base.hasMore,
    // a window that ends before the present still does (an older page does not change that)
    ...(base.hasNewer ? { hasNewer: true } : {}),
    // a live message on a stale channel does not make the rest current
    ...(base.stale ? { stale: true } : {}),
  };
}

function sameEmoji(a: Emoji, b: Emoji): boolean {
  return a.id ? a.id === b.id : !b.id && a.name === b.name;
}

function reactionAdd(m: Message, emoji: Emoji, mine: boolean): Message {
  const list = (m.reactions ?? []).slice();
  const i = list.findIndex((r) => sameEmoji(r.emoji, emoji));
  if (i >= 0) {
    const r = list[i] as Reaction;
    if (mine && r.me) return m;
    list[i] = { ...r, count: r.count + 1, me: r.me || mine };
  } else list.push({ emoji, count: 1, me: mine });
  return { ...m, reactions: list };
}

function reactionRemove(m: Message, emoji: Emoji, mine: boolean): Message {
  const list = (m.reactions ?? []).slice();
  const i = list.findIndex((r) => sameEmoji(r.emoji, emoji));
  if (i < 0) return m;
  const r = list[i] as Reaction;
  if (mine && !r.me) return m;
  const count = r.count - 1;
  if (count <= 0) list.splice(i, 1);
  else list[i] = { ...r, count, me: mine ? false : r.me };
  return { ...m, reactions: list };
}

function patchMessage(
  s: DataState,
  channelId: Snowflake,
  id: Snowflake,
  fn: (m: Message) => Message,
): DataState {
  const cm = s.messages[channelId];
  const m = cm?.byId[id];
  if (!cm || !m) return s;
  return {
    ...s,
    messages: { ...s.messages, [channelId]: { ...cm, byId: { ...cm.byId, [id]: fn(m) } } },
  };
}

function setVoiceState(s: DataState, vs: VoiceState): DataState {
  const g = vs.guild_id ?? '';
  const cur = { ...(s.voiceStates[g] ?? {}) };
  if (vs.channel_id) cur[vs.user_id] = vs;
  else delete cur[vs.user_id];
  let users = s.users;
  if (vs.member) users = withUser(users, vs.member.user);
  return { ...s, users, voiceStates: { ...s.voiceStates, [g]: cur } };
}

/**
 * Applies one dispatch frame. Unknown events leave the state as it was.
 * `isIgnored` tells who the user ignores (store/um.ts): their messages never
 * raise a count (user-management §3.2, as the server counts).
 */
export function applyDispatch(
  s: DataState,
  frame: GatewayDispatch,
  now: number = Date.now(),
  isIgnored: (userId: Snowflake) => boolean = () => false,
): DataState {
  switch (frame.t) {
    case 'READY': {
      const d = frame.d;
      let next: DataState = emptyData();
      next.me = d.user;
      next.settings = d.user_settings;
      next.users = { [d.user.id]: d.user };
      for (const g of d.guilds) next = withGuild(next, g);
      let users = next.users;
      for (const c of d.private_channels) {
        next.channels[c.id] = c;
        for (const r of c.recipients ?? []) users = withUser(users, r);
      }
      for (const r of d.relationships) {
        next.relationships[r.id] = r;
        users = withUser(users, r.user);
      }
      for (const p of d.presences) next.presences[p.user.id] = p;
      for (const r of d.read_state) next.readStates[r.id] = r;
      for (const g of d.user_guild_settings?.entries ?? []) next.guildSettings[g.guild_id] = g;
      next.users = users;
      // Keep loaded history on screen across a fresh READY, but stale: this is
      // a new session (the resume window passed), so whatever was sent, edited
      // or deleted meanwhile never arrived. The next load fetches it.
      next.messages = {};
      for (const [id, cm] of Object.entries(s.messages))
        next.messages[id] = cm.loaded ? { ...cm, stale: true } : cm;
      // a role preview survives a reconnect; the roles it names are checked on use
      next.viewAs = s.viewAs;
      return next;
    }
    case 'RESUMED':
      return s;
    case 'GUILD_CREATE': {
      // joined while connected (an invite, a rejoin after a kick, ban or leave): the whole guild
      const next = withGuild(s, frame.d);
      let messages: DataState['messages'] | null = null;
      for (const c of frame.d.channels) {
        const cm = s.messages[c.id];
        if (!cm?.loaded) continue;
        // anything may have happened while we were out: shown, but fetched again
        messages ??= { ...s.messages };
        messages[c.id] = { ...cm, stale: true };
      }
      return messages ? { ...next, messages } : next;
    }
    case 'USER_UPDATE': {
      const me = s.me && s.me.id === frame.d.id ? { ...s.me, ...frame.d } : s.me;
      return { ...s, me, users: withUser(s.users, frame.d) };
    }
    case 'USER_SETTINGS_UPDATE':
      return s.settings ? { ...s, settings: { ...s.settings, ...frame.d } } : s;
    // Discord's GUILD_DELETE (Console v2): you left or were removed, or the server was deleted
    case 'GUILD_DELETE':
      return s.guilds[frame.d.id] ? dropGuild(s, frame.d.id) : s;
    case 'GUILD_UPDATE': {
      const prev = s.guilds[frame.d.id];
      return { ...s, guilds: { ...s.guilds, [frame.d.id]: { ...prev, ...frame.d } } };
    }
    case 'MESSAGE_CREATE': {
      const m = frame.d;
      const cm = s.messages[m.channel_id];
      let next: DataState = { ...s, users: withMessageUsers(s.users, m) };
      // only append to a channel whose latest page is loaded; otherwise the next fetch brings it
      if (cm?.loaded && !cm.hasNewer)
        next = { ...next, messages: { ...next.messages, [m.channel_id]: mergeMessages(cm, [m]) } };
      const ch = s.channels[m.channel_id];
      const inThread = !!ch && isThreadChannelType(ch.type);
      if (ch)
        next.channels = {
          ...next.channels,
          [m.channel_id]: { ...(inThread ? bumpThread(ch) : ch), last_message_id: m.id },
        };
      // the author stops typing
      const ty = s.typing[m.channel_id];
      if (ty && ty[m.author.id]) {
        const rest = { ...ty };
        delete rest[m.author.id];
        next.typing = { ...next.typing, [m.channel_id]: rest };
      }
      // mentions of me raise the unread mention count, by the server's own rule
      // (messages.service notify): never from someone I ignore or blocked, never
      // a silent message, never in a message request (pending or dismissed)
      if (s.me && m.author.id !== s.me.id && !isIgnored(m.author.id)) {
        const silent =
          (m.flags & MessageFlags.SUPPRESS_NOTIFICATIONS) !== 0 ||
          s.relationships[m.author.id]?.type === RelationshipType.BLOCKED ||
          (!!ch?.nx_request_state && ch.nx_request_state !== 'accepted');
        // in a thread, @everyone and roles ping only its members (the server's rule)
        const wide = !inThread || !!ch?.member;
        // ...and, in a server, only as the notification settings allow (notify/rules.ts)
        const mentioned =
          !silent &&
          ((ch && (ch.type === 1 || ch.type === 3)) ||
            mentionAllowed(
              s,
              // a thread follows its parent channel's settings, as on the server
              inThread && ch?.parent_id ? (s.channels[ch.parent_id] ?? ch) : ch,
              mentionKindsOf(s, m, ch).filter((k) => wide || k === 'user'),
            ));
        if (mentioned) {
          const rs = s.readStates[m.channel_id] ?? {
            id: m.channel_id,
            last_message_id: null,
            mention_count: 0,
          };
          next.readStates = {
            ...next.readStates,
            [m.channel_id]: { ...rs, mention_count: rs.mention_count + 1 },
          };
        }
      } else if (s.me && m.author.id === s.me.id) {
        // my own message marks the channel read
        const rs = s.readStates[m.channel_id];
        next.readStates = {
          ...next.readStates,
          [m.channel_id]: {
            id: m.channel_id,
            last_message_id: m.id,
            mention_count: 0,
            last_pin_timestamp: rs?.last_pin_timestamp,
          },
        };
      }
      return next;
    }
    case 'MESSAGE_UPDATE': {
      const d = frame.d;
      // a poll's counts come for everyone: your own votes stay marked (store/polls.ts)
      const next = patchMessage(s, d.channel_id, d.id, (m) => ({ ...m, ...keepMyVotes(m, d) }));
      // replies quote the edited text too
      const cm = next.messages[d.channel_id];
      if (!cm) return next;
      let byId: ChannelMessages['byId'] | null = null;
      for (const [k, m] of Object.entries(cm.byId)) {
        if (m.referenced_message?.id !== d.id) continue;
        byId ??= { ...cm.byId };
        byId[k] = { ...m, referenced_message: { ...m.referenced_message, ...d } };
      }
      return byId
        ? { ...next, messages: { ...next.messages, [d.channel_id]: { ...cm, byId } } }
        : next;
    }
    case 'MESSAGE_DELETE': {
      // a thread counts one fewer, its history loaded or not (as the server counts)
      const st = threadCountDown(s, frame.d.channel_id);
      const cm = st.messages[frame.d.channel_id];
      if (!cm || !cm.byId[frame.d.id]) return st;
      const byId = { ...cm.byId };
      delete byId[frame.d.id];
      // replies to it now point at a deleted message
      for (const [k, m] of Object.entries(byId)) {
        if (m.referenced_message && m.referenced_message.id === frame.d.id)
          byId[k] = { ...m, referenced_message: null };
      }
      return {
        ...st,
        messages: {
          ...st.messages,
          [frame.d.channel_id]: { ...cm, ids: cm.ids.filter((x) => x !== frame.d.id), byId },
        },
      };
    }
    // Discord's MESSAGE_DELETE_BULK (Console v2): a purge, or a ban that deletes messages
    case 'MESSAGE_DELETE_BULK': {
      const gone = new Set(frame.d.ids);
      let st = s;
      // a thread counts each one fewer, its history loaded or not (as MESSAGE_DELETE)
      for (let i = 0; i < frame.d.ids.length; i++) st = threadCountDown(st, frame.d.channel_id);
      const cm = st.messages[frame.d.channel_id];
      if (!cm || !cm.ids.some((x) => gone.has(x))) return st;
      const byId = { ...cm.byId };
      for (const id of gone) delete byId[id];
      // replies to them now point at a deleted message
      for (const [k, m] of Object.entries(byId)) {
        if (m.referenced_message && gone.has(m.referenced_message.id))
          byId[k] = { ...m, referenced_message: null };
      }
      return {
        ...st,
        messages: {
          ...st.messages,
          [frame.d.channel_id]: { ...cm, ids: cm.ids.filter((x) => !gone.has(x)), byId },
        },
      };
    }
    case 'MESSAGE_REACTION_ADD': {
      const d = frame.d;
      const mine = d.user_id === s.me?.id;
      return patchMessage(s, d.channel_id, d.message_id, (m) => reactionAdd(m, d.emoji, mine));
    }
    case 'MESSAGE_REACTION_REMOVE': {
      const d = frame.d;
      const mine = d.user_id === s.me?.id;
      return patchMessage(s, d.channel_id, d.message_id, (m) => reactionRemove(m, d.emoji, mine));
    }
    case 'MESSAGE_POLL_VOTE_ADD':
    case 'MESSAGE_POLL_VOTE_REMOVE': {
      const d = frame.d;
      const add = frame.t === 'MESSAGE_POLL_VOTE_ADD';
      return patchMessage(s, d.channel_id, d.message_id, (m) =>
        applyPollVote(m, d, d.user_id === s.me?.id, add),
      );
    }
    case 'TYPING_START': {
      const d = frame.d;
      if (d.user_id === s.me?.id) return s;
      const ends = Math.max(now, d.timestamp * 1000) + TYPING_MS;
      let users = s.users;
      if (d.member) users = withUser(users, d.member.user);
      return {
        ...s,
        users,
        typing: {
          ...s.typing,
          [d.channel_id]: { ...(s.typing[d.channel_id] ?? {}), [d.user_id]: ends },
        },
      };
    }
    case 'CHANNEL_CREATE':
    case 'CHANNEL_UPDATE': {
      const c = frame.d;
      let users = s.users;
      for (const r of c.recipients ?? []) users = withUser(users, r);
      const next = { ...s.channels[c.id], ...c };
      // a DM created for you is in your DM list: a request state it had here is over
      if (frame.t === 'CHANNEL_CREATE' && !c.guild_id) next.nx_request_state = c.nx_request_state;
      return { ...s, users, channels: { ...s.channels, [c.id]: next } };
    }
    case 'CHANNEL_DELETE': {
      // its threads go with it
      const channels = { ...dropThreadsOf(s.channels, frame.d.id) };
      delete channels[frame.d.id];
      const messages = { ...s.messages };
      delete messages[frame.d.id];
      return { ...s, channels, messages };
    }
    case 'CHANNEL_PINS_UPDATE': {
      const ch = s.channels[frame.d.channel_id];
      return {
        ...s,
        channels: ch
          ? { ...s.channels, [ch.id]: { ...ch, last_pin_timestamp: frame.d.last_pin_timestamp } }
          : s.channels,
        pinsVersion: {
          ...s.pinsVersion,
          [frame.d.channel_id]: (s.pinsVersion[frame.d.channel_id] ?? 0) + 1,
        },
      };
    }
    case 'GUILD_MEMBER_ADD': {
      const { guild_id, ...member } = frame.d;
      return {
        ...s,
        users: withUser(s.users, member.user),
        members: {
          ...s.members,
          [guild_id]: { ...(s.members[guild_id] ?? {}), [member.user.id]: member },
        },
      };
    }
    case 'GUILD_MEMBER_UPDATE': {
      const d = frame.d;
      const prev = s.members[d.guild_id]?.[d.user.id];
      const member: GuildMember = {
        deaf: false,
        mute: false,
        flags: 0,
        ...prev,
        user: d.user,
        nick: d.nick,
        avatar: d.avatar,
        roles: d.roles,
        joined_at: d.joined_at,
        communication_disabled_until: d.communication_disabled_until,
        ...(d.deaf !== undefined ? { deaf: d.deaf } : {}),
        ...(d.mute !== undefined ? { mute: d.mute } : {}),
      };
      // Server mute and deafen live on the member and on their voice state:
      // keep a voice state this client already has in step (the server's
      // VOICE_STATE_UPDATE follows; the menus and lists read the state).
      const vs = s.voiceStates[d.guild_id]?.[d.user.id];
      const mute = d.mute ?? vs?.mute;
      const deaf = d.deaf ?? vs?.deaf;
      const voiceStates =
        vs && (vs.mute !== mute || vs.deaf !== deaf)
          ? {
              ...s.voiceStates,
              [d.guild_id]: {
                ...s.voiceStates[d.guild_id],
                [d.user.id]: { ...vs, mute: !!mute, deaf: !!deaf },
              },
            }
          : s.voiceStates;
      return {
        ...s,
        users: withUser(s.users, d.user),
        members: {
          ...s.members,
          [d.guild_id]: { ...(s.members[d.guild_id] ?? {}), [d.user.id]: member },
        },
        voiceStates,
      };
    }
    case 'GUILD_MEMBER_REMOVE': {
      // kicked, banned or left: the server forgets this guild for us, so do we
      if (s.me && frame.d.user.id === s.me.id) return dropGuild(s, frame.d.guild_id);
      const g = { ...(s.members[frame.d.guild_id] ?? {}) };
      delete g[frame.d.user.id];
      return { ...s, members: { ...s.members, [frame.d.guild_id]: g } };
    }
    case 'GUILD_ROLE_CREATE':
    case 'GUILD_ROLE_UPDATE': {
      const g = s.guilds[frame.d.guild_id];
      if (!g) return s;
      const roles = g.roles.filter((r) => r.id !== frame.d.role.id).concat(frame.d.role);
      return { ...s, guilds: { ...s.guilds, [g.id]: { ...g, roles } } };
    }
    case 'GUILD_ROLE_DELETE': {
      const g = s.guilds[frame.d.guild_id];
      if (!g) return s;
      const roleId = frame.d.role_id;
      const roles = g.roles.filter((r) => r.id !== roleId);
      const mm = { ...(s.members[g.id] ?? {}) };
      for (const [k, m] of Object.entries(mm))
        if (m.roles.includes(roleId)) mm[k] = { ...m, roles: m.roles.filter((r) => r !== roleId) };
      return {
        ...s,
        guilds: { ...s.guilds, [g.id]: { ...g, roles } },
        members: { ...s.members, [g.id]: mm },
      };
    }
    case 'PRESENCE_UPDATE': {
      const p = frame.d;
      const prev = s.presences[p.user.id];
      let users = s.users;
      if (p.user.username)
        users = withUser(users, { ...(s.users[p.user.id] as User), ...p.user } as User);
      return { ...s, users, presences: { ...s.presences, [p.user.id]: { ...prev, ...p } } };
    }
    case 'VOICE_STATE_UPDATE':
      return setVoiceState(s, frame.d);
    case 'RELATIONSHIP_ADD':
      return {
        ...s,
        users: withUser(s.users, frame.d.user),
        relationships: { ...s.relationships, [frame.d.id]: frame.d },
      };
    case 'RELATIONSHIP_REMOVE': {
      const r = { ...s.relationships };
      delete r[frame.d.id];
      return { ...s, relationships: r };
    }
    case 'CHANNEL_RECIPIENT_ADD':
    case 'CHANNEL_RECIPIENT_REMOVE': {
      const c = s.channels[frame.d.channel_id];
      if (!c) return s;
      const rest = (c.recipients ?? []).filter((u) => u.id !== frame.d.user.id);
      const recipients = frame.t === 'CHANNEL_RECIPIENT_ADD' ? rest.concat(frame.d.user) : rest;
      return { ...s, channels: { ...s.channels, [c.id]: { ...c, recipients } } };
    }
    case 'MESSAGE_ACK': {
      const d = frame.d;
      return {
        ...s,
        readStates: {
          ...s.readStates,
          [d.channel_id]: {
            ...s.readStates[d.channel_id],
            id: d.channel_id,
            last_message_id: d.message_id,
            mention_count: d.mention_count ?? 0,
          },
        },
      };
    }
    // threads (2026-10-29): threads/store.ts
    case 'THREAD_CREATE':
    case 'THREAD_UPDATE':
    case 'THREAD_DELETE':
    case 'THREAD_MEMBERS_UPDATE':
      return applyThreadDispatch(s, frame);
    case 'USER_GUILD_SETTINGS_UPDATE':
      return { ...s, guildSettings: { ...s.guildSettings, [frame.d.guild_id]: frame.d } };
    case 'NX_MESSAGE_REQUEST_UPDATE': {
      // the DM stays out of the DM list until it is accepted (selectors.privateChannels)
      const d = frame.d;
      const prev = d.channel ?? s.channels[d.channel_id];
      if (!prev) return s;
      let users = s.users;
      for (const u of d.channel?.recipients ?? []) users = withUser(users, u);
      return {
        ...s,
        users,
        channels: {
          ...s.channels,
          [d.channel_id]: { ...prev, nx_request_state: d.request_state },
        },
      };
    }
    default:
      return s;
  }
}

/** Drops typing indicators that ended. Returns the same object when nothing changed. */
export function expireTyping(s: DataState, now: number): DataState {
  let changed = false;
  const typing: DataState['typing'] = {};
  for (const [ch, who] of Object.entries(s.typing)) {
    const keep: Record<Snowflake, number> = {};
    for (const [u, until] of Object.entries(who)) {
      if (until > now) keep[u] = until;
      else changed = true;
    }
    if (Object.keys(keep).length) typing[ch] = keep;
  }
  return changed ? { ...s, typing } : s;
}

/** A page of history arrived (newest first from the API). */
export function applyHistory(
  s: DataState,
  channelId: Snowflake,
  page: Message[],
  limit: number,
  older: boolean,
): DataState {
  let users = s.users;
  for (const m of page) users = withMessageUsers(users, m);
  const prev = s.messages[channelId];
  if (!older && prev?.stale) {
    const cm = reconcileStale(prev, page, limit);
    return { ...s, users, messages: { ...s.messages, [channelId]: cm } };
  }
  // an older page, or the first page, tells whether more history exists
  const hasMore = older || !prev?.loaded ? page.length >= limit : prev.hasMore;
  let cm = mergeMessages(prev, page, { loaded: true, hasMore });
  // scrolled far up: the newest end goes (it loads again as you scroll back down)
  if (older) cm = trimWindow(cm, 'older');
  return { ...s, users, messages: { ...s.messages, [channelId]: cm } };
}

/**
 * The most messages one channel keeps loaded. Discord keeps a window too:
 * without one, scrolling up a long channel kept every page in memory and on
 * screen (1500 messages were 68k DOM nodes and 320 MB in the tab).
 */
export const MAX_LOADED = 150;

/**
 * Cuts a channel's window to `max` messages. 'older' keeps the oldest end
 * (an older page arrived: the reader is up there), so the window then has
 * newer history after it (`hasNewer`, "viewing older messages"); 'newer'
 * keeps the newest end, so more older history exists again (`hasMore`).
 */
export function trimWindow(
  cm: ChannelMessages,
  keep: 'older' | 'newer',
  max = MAX_LOADED,
): ChannelMessages {
  if (cm.ids.length <= max) return cm;
  const ids = keep === 'older' ? cm.ids.slice(0, max) : cm.ids.slice(-max);
  const byId: Record<Snowflake, Message> = {};
  for (const id of ids) byId[id] = cm.byId[id]!;
  return keep === 'older'
    ? { ...cm, ids, byId, hasNewer: true }
    : { ...cm, ids, byId, hasMore: true };
}

/**
 * A channel the reader left, or one pinned to its end that grew past the
 * window: only its newest `max` messages stay loaded. A window of older
 * messages ("viewing older messages") is dropped; opening the channel loads
 * the present again anyway.
 */
export function trimLoaded(s: DataState, channelId: Snowflake, max: number): DataState {
  const cm = s.messages[channelId];
  if (!cm) return s;
  if (cm.hasNewer) {
    const messages = { ...s.messages };
    delete messages[channelId];
    return { ...s, messages };
  }
  const next = trimWindow(cm, 'newer', max);
  return next === cm ? s : { ...s, messages: { ...s.messages, [channelId]: next } };
}

/**
 * The page around a message arrived (a jump to it; newest first, `limit`
 * long, about half older than it). When the page reaches the newest
 * message it joins the history already loaded (both end at the present);
 * otherwise it replaces it, as a window with newer history after it.
 */
export function applyAround(
  s: DataState,
  channelId: Snowflake,
  targetId: Snowflake,
  page: Message[],
  limit: number,
): DataState {
  let users = s.users;
  for (const m of page) users = withMessageUsers(users, m);
  const half = Math.floor(limit / 2);
  const older = page.filter((m) => compareSnowflakes(m.id, targetId) < 0).length;
  const newer = page.length - older;
  const hasMore = older >= half;
  const hasNewer = newer >= limit - half;
  const prev = s.messages[channelId];
  let cm: ChannelMessages;
  if (!hasNewer && prev?.loaded && !prev.hasNewer) {
    const merged = mergeMessages(prev, page);
    // the older end is the page's when it reaches further back than what was loaded
    const startsOlder = !prev.ids.length || merged.ids[0] !== prev.ids[0];
    cm = {
      ...merged,
      loaded: true,
      hasMore: startsOlder ? hasMore : prev.hasMore,
      hasNewer: false,
    };
  } else {
    cm = { ...mergeMessages(undefined, page), loaded: true, hasMore, hasNewer };
  }
  // joined to a long loaded history: the window keeps the newest end if the target is in it
  const newest = trimWindow(cm, 'newer');
  cm = newest.byId[targetId] ? newest : trimWindow(cm, 'older');
  return { ...s, users, messages: { ...s.messages, [channelId]: cm } };
}

/** A page of newer history arrived (oldest-first window after a jump; newest first from the API). */
export function applyNewer(
  s: DataState,
  channelId: Snowflake,
  page: Message[],
  limit: number,
): DataState {
  const prev = s.messages[channelId];
  if (!prev?.loaded) return s;
  let users = s.users;
  for (const m of page) users = withMessageUsers(users, m);
  // scrolling back down: the oldest end goes
  const cm = trimWindow({ ...mergeMessages(prev, page), hasNewer: page.length >= limit }, 'newer');
  return { ...s, users, messages: { ...s.messages, [channelId]: cm } };
}

/**
 * The latest page after a fresh READY, laid over what was kept: inside the
 * page's range the page is the truth (missing = deleted meanwhile); a page
 * shorter than the limit is the whole history; a full page that does not
 * reach back to what we had leaves an unknown gap, so the older copy goes
 * (scrolling up fetches it again). Messages newer than the page (they
 * arrived while it loaded) stay.
 */
function reconcileStale(prev: ChannelMessages, page: Message[], limit: number): ChannelMessages {
  const ids = sortIds(page.map((m) => m.id));
  const oldest = ids[0];
  const newest = ids[ids.length - 1];
  const full = page.length >= limit;
  const inPage = new Set(ids);
  const within = (id: Snowflake) =>
    !!oldest &&
    !!newest &&
    compareSnowflakes(id, oldest) >= 0 &&
    compareSnowflakes(id, newest) <= 0;
  const overlaps = full && prev.ids.some(within);
  const keep = prev.ids.filter((id) => {
    if (newest && compareSnowflakes(id, newest) > 0) return true;
    if (within(id)) return inPage.has(id);
    return overlaps;
  });
  const byId: Record<Snowflake, Message> = {};
  for (const id of keep) byId[id] = prev.byId[id]!;
  for (const m of page) byId[m.id] = m;
  return {
    ids: sortIds([...new Set([...keep, ...ids])]),
    byId,
    loaded: true,
    hasMore: full ? (overlaps ? prev.hasMore : true) : false,
  };
}

/** Puts a message the API returned (send, edit) into the loaded history. */
export function upsertMessage(s: DataState, m: Message): DataState {
  const cm = s.messages[m.channel_id];
  return { ...s, messages: { ...s.messages, [m.channel_id]: mergeMessages(cm, [m]) } };
}

export function removeMessage(s: DataState, channelId: Snowflake, id: Snowflake): DataState {
  return applyDispatch(s, { op: 0, s: 0, t: 'MESSAGE_DELETE', d: { id, channel_id: channelId } });
}

/** True when the channel has messages after the read position. */
export function isUnread(s: DataState, channelId: Snowflake): boolean {
  const c = s.channels[channelId];
  if (!c?.last_message_id) return false;
  const rs = s.readStates[channelId];
  if (!rs?.last_message_id) return true;
  return compareSnowflakes(c.last_message_id, rs.last_message_id) > 0;
}
